/**
 * Issues — every error from every client account, one live feed.
 *
 * The rule this page enforces: if a CLIENT can see an error (the red sync
 * pill), the OPERATOR must be able to see it here — with the actual error
 * text, not just a count. Four sources, served by GET /admin/issues:
 * failed sync jobs (deduped by client + job, with first/last seen), broken
 * platform connections, records stuck in the ingest retry queue, and
 * automatic app-crash reports.
 *
 * Active issues sort first; resolved ones (a later successful run cleared
 * them) stay visible greyed-out for 7 days — a job that fails and recovers
 * every night is a real problem that a "current errors only" view would hide.
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useApiClient } from "../lib/api-context.js";
import { useToast } from "../lib/toast.js";
import { Card } from "../components/cards.js";
import { Segmented } from "../components/Segmented.js";
import { useAccountChange } from "../lib/account-context.js";

interface Issue {
  id: string;
  kind: "job_failure" | "integration_error" | "ingest_failures" | "ui_crash" | "data_check";
  accountId: string;
  accountName: string;
  title: string;
  errorMessage: string | null;
  jobType?: string;
  platform?: string;
  entityType?: string;
  count: number;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  active: boolean;
  severity: "high" | "medium" | "resolved";
}

interface IssuesPayload {
  issues: Issue[];
  activeCount: number;
}

type ShowTab = "active" | "all";

const KIND_LABEL: Record<Issue["kind"], string> = {
  job_failure: "sync job",
  integration_error: "connection",
  ingest_failures: "stuck records",
  ui_crash: "app crash",
  data_check: "saved data check",
};

export default function AgencyIssuesPage(): React.ReactElement {
  const api = useApiClient();
  const changeAccount = useAccountChange();
  const toast = useToast();
  const [show, setShow] = useState<ShowTab>("active");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [switching, setSwitching] = useState<string | null>(null);

  const status = useQuery({
    queryKey: ["account", "agency-status"],
    queryFn: () => api.get<{ isOperator: boolean }>("/account/agency-status"),
  });
  const isOperator = status.data?.isOperator ?? false;

  const q = useQuery({
    queryKey: ["admin", "issues"],
    enabled: isOperator,
    queryFn: () => api.get<IssuesPayload>("/admin/issues"),
    refetchInterval: 60_000, // errors move on their own — keep the feed live
  });
  const issues = useMemo(() => q.data?.issues ?? [], [q.data]);
  const visible = show === "active" ? issues.filter((i) => i.active) : issues;
  const resolvedCount = issues.length - (q.data?.activeCount ?? 0);

  const switchTo = async (accountId: string): Promise<void> => {
    setSwitching(accountId);
    try {
      await changeAccount({
        path: "/account/switch",
        body: { accountId },
        targetAccountId: accountId,
        destination: "/dashboard",
      });
    } catch {
      setSwitching(null);
      toast.error("Couldn't switch into that account.");
    }
  };

  if (status.isLoading) return <p style={{ color: "var(--body)" }}>Loading…</p>;
  if (!isOperator) {
    return (
      <div>
        <h1 style={{ margin: 0, fontSize: 24 }}>Issues</h1>
        <p style={{ color: "var(--body)", marginTop: 10 }}>This area is only available to agency operators.</p>
      </div>
    );
  }

  return (
    <div>
      <div style={kicker}>Agency</div>
      <h1 style={{ margin: "7px 0 8px", fontSize: 27 }}>What's broken right now — across every client?</h1>
      <p style={{ color: "var(--body)", fontSize: 14.5, maxWidth: 820, lineHeight: 1.5, marginTop: 0 }}>
        Data checks, failed data pulls, broken connections, stuck records, and app crashes across your clients.
        Saved data checks show when they ran. Open Data health to check them again and review the result.
      </p>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 14, alignItems: "center" }}>
        <Segmented<ShowTab>
          value={show}
          onChange={setShow}
          options={[
            { value: "active", label: `Active${q.data ? ` (${q.data.activeCount})` : ""}` },
            { value: "all", label: `All incl. resolved${resolvedCount > 0 ? ` (+${resolvedCount})` : ""}` },
          ]}
        />
      </div>

      {q.isLoading && <p style={{ marginTop: 18, color: "var(--body)" }}>Checking every client for errors…</p>}
      {q.isError && <p style={{ marginTop: 18, color: "var(--bad)" }}>Couldn't load the issues feed. Refresh to retry.</p>}

      {q.data && visible.length === 0 && (
        <Card style={{ marginTop: 16 }}>
          <p style={{ color: "var(--body)", fontSize: 13.5, margin: 0 }}>
            {show === "active"
              ? "No open issues were found. This does not confirm that every client's setup and financial review are complete."
              : "No saved check findings or recent errors were found."}
          </p>
        </Card>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 16 }}>
        {visible.map((issue) => {
          const isOpen = expanded === issue.id;
          return (
            <Card key={issue.id} style={{ padding: 0, opacity: issue.active ? 1 : 0.6 }}>
              <button type="button" onClick={() => setExpanded(isOpen ? null : issue.id)} style={rowBtn} aria-expanded={isOpen}>
                <span
                  style={{
                    ...sevDot,
                    background: !issue.active ? "var(--body-faint)" : issue.severity === "high" ? "var(--bad)" : "#E5A83B",
                  }}
                />
                <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                  <div style={{ fontSize: 13.5, color: "var(--text)", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {issue.accountName} — {issue.title}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--body)", marginTop: 2 }}>
                    {KIND_LABEL[issue.kind]}
                    {issue.count > 1 ? ` · ${issue.count}×` : ""}
                    {issue.firstSeenAt ? ` · first ${when(issue.firstSeenAt)}` : ""}
                    {issue.lastSeenAt ? ` · last ${when(issue.lastSeenAt)}` : ""}
                  </div>
                </div>
                <span style={stateChip(issue)}>{issue.active ? (issue.severity === "high" ? "broken" : "needs a look") : "resolved"}</span>
                <span style={{ color: "var(--body-faint)", fontSize: 12 }}>{isOpen ? "▲" : "▼"}</span>
              </button>

              {isOpen && (
                <div style={{ padding: "0 16px 14px" }}>
                  <div style={detailBlock}>
                    <div style={detailLabel}>{issue.kind === "data_check" ? "What the saved check found" : "The error, verbatim"}</div>
                    {issue.errorMessage ? (
                      <pre style={errorText}>{issue.errorMessage}</pre>
                    ) : (
                      <p style={{ fontSize: 12.5, color: "var(--body)", margin: 0 }}>No error text was recorded for this one.</p>
                    )}
                    {!issue.active && (
                      <p style={{ fontSize: 12, color: "var(--body)", margin: "8px 0 0" }}>
                        A later run succeeded, so this cleared itself. It stays listed for 7 days — a job that fails and
                        recovers on repeat is worth a look anyway.
                      </p>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                    {issue.kind === "data_check" && <Link to={`/agency/data-health?account=${encodeURIComponent(issue.accountId)}`} style={ghostBtn}>Review in Data health →</Link>}
                    <button type="button" style={ghostBtn} disabled={switching !== null} onClick={() => void switchTo(issue.accountId)}>
                      {switching === issue.accountId ? "Switching…" : "Switch into this client →"}
                    </button>
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function when(iso: string): string {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 60) return `${Math.max(mins, 1)}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

const kicker: React.CSSProperties = { color: "var(--accent)", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" };
const rowBtn: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "13px 16px", background: "transparent",
  border: "none", cursor: "pointer", fontFamily: "inherit",
};
const sevDot: React.CSSProperties = { width: 9, height: 9, borderRadius: "50%", flexShrink: 0 };
const stateChip = (issue: Issue): React.CSSProperties => ({
  fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 9, whiteSpace: "nowrap", flexShrink: 0,
  background: !issue.active
    ? "var(--surface-2)"
    : issue.severity === "high"
      ? "color-mix(in srgb, var(--bad) 14%, transparent)"
      : "color-mix(in srgb, #E5A83B 16%, transparent)",
  color: !issue.active ? "var(--body)" : issue.severity === "high" ? "var(--bad)" : "#B07E18",
});
const detailBlock: React.CSSProperties = { padding: "12px 14px", background: "var(--surface-2)", borderRadius: "var(--r-sm)", border: "1px solid var(--border)" };
const detailLabel: React.CSSProperties = { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--body-faint)", marginBottom: 6 };
const errorText: React.CSSProperties = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12, color: "var(--text)",
  whiteSpace: "pre-wrap", overflowWrap: "anywhere", margin: 0, maxHeight: 260, overflowY: "auto",
};
const ghostBtn: React.CSSProperties = {
  background: "transparent", border: "1px solid var(--border)", color: "var(--accent)", borderRadius: "var(--r-sm)",
  padding: "8px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
};
